import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { ConfigService } from 'src/app/_services/config.service';

@Component({
  standalone: false,
	templateUrl: '503.component.html',
	styles: [
		`
			.server-down-container {
				min-height: 100vh;
				display: flex;
				align-items: center;
				justify-content: center;
				background: linear-gradient(135deg, #667eea 0%, rgb(75, 126, 162) 100%);
				font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
				padding: 20px;
			}

			.error-content {
				background: white;
				border-radius: 16px;
				padding: 48px;
				max-width: 600px;
				width: 100%;
				text-align: center;
				box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
				animation: slideUp 0.6s ease-out;
			}

			@keyframes slideUp {
				from {
					opacity: 0;
					transform: translateY(30px);
				}
				to {
					opacity: 1;
					transform: translateY(0);
				}
			}

			.error-icon {
				color: #f56565;
				margin-bottom: 24px;
				animation: pulse 2s infinite;
			}

			@keyframes pulse {
				0%,
				100% {
					opacity: 1;
				}
				50% {
					opacity: 0.7;
				}
			}

			.error-title {
				font-size: 32px;
				font-weight: 700;
				color: #2d3748;
				margin-bottom: 16px;
				line-height: 1.2;
			}

			.error-message {
				font-size: 18px;
				color: #4a5568;
				margin-bottom: 32px;
				line-height: 1.6;
			}

			.error-details {
				background: #f7fafc;
				border-radius: 8px;
				padding: 20px;
				margin-bottom: 32px;
				text-align: left;
			}

			.detail-item {
				margin-bottom: 8px;
				font-size: 14px;
				color: #4a5568;
			}

			.detail-item:last-child {
				margin-bottom: 0;
			}

			.action-buttons {
				display: flex;
				gap: 16px;
				justify-content: center;
				margin-bottom: 32px;
				flex-wrap: wrap;
			}

			.retry-btn,
			.refresh-btn {
				padding: 12px 24px;
				border-radius: 8px;
				font-weight: 600;
				font-size: 16px;
				border: none;
				cursor: pointer;
				transition: all 0.3s ease;
				min-width: 140px;
			}

			.retry-btn.primary {
				background: #4299e1;
				color: white;
			}

			.retry-btn.primary:hover:not(:disabled) {
				background: #3182ce;
				transform: translateY(-2px);
			}

			.retry-btn:disabled {
				opacity: 0.6;
				cursor: not-allowed;
				transform: none;
			}

			.refresh-btn.secondary {
				background: #e2e8f0;
				color: #4a5568;
			}

			.refresh-btn.secondary:hover {
				background: #cbd5e0;
				transform: translateY(-2px);
			}

			.loading {
				display: flex;
				align-items: center;
				justify-content: center;
				gap: 8px;
			}

			.spinner {
				width: 16px;
				height: 16px;
				border: 2px solid transparent;
				border-top: 2px solid currentColor;
				border-radius: 50%;
				animation: spin 1s linear infinite;
			}

			@keyframes spin {
				to {
					transform: rotate(360deg);
				}
			}

			.auto-retry {
				background: #ebf8ff;
				border: 1px solid #bee3f8;
				border-radius: 8px;
				padding: 16px;
				margin-bottom: 32px;
			}

			.auto-retry-info {
				display: flex;
				align-items: center;
				justify-content: center;
				gap: 8px;
				color: #2b6cb0;
				font-size: 14px;
				margin-bottom: 8px;
			}

			.disable-auto-retry {
				background: none;
				border: none;
				color: #2b6cb0;
				font-size: 12px;
				cursor: pointer;
				text-decoration: underline;
			}

			.help-section {
				text-align: left;
				margin-bottom: 24px;
			}

			.help-section h3 {
				color: #2d3748;
				font-size: 18px;
				margin-bottom: 12px;
			}

			.help-section ul {
				color: #4a5568;
				line-height: 1.6;
			}

			.help-section li {
				margin-bottom: 4px;
			}

			.contact-info {
				padding-top: 24px;
				border-top: 1px solid #e2e8f0;
				color: #4a5568;
			}

			.contact-info a {
				color: #4299e1;
				text-decoration: none;
			}

			.contact-info a:hover {
				text-decoration: underline;
			}

			@media (max-width: 768px) {
				.error-content {
					padding: 32px 24px;
				}

				.error-title {
					font-size: 24px;
				}

				.error-message {
					font-size: 16px;
				}

				.action-buttons {
					flex-direction: column;
					align-items: center;
				}

				.retry-btn,
				.refresh-btn {
					width: 100%;
					max-width: 200px;
				}
			}
		`,
	],
})
export class P503Component implements OnInit, OnDestroy {
	lastChecked: Date = new Date();
	isRetrying: boolean = false;
	nextRetryIn: number = 0;
	autoRetryEnabled: boolean = true;
	autoRetryCountdown: number = 30;

	private retrySubscription?: Subscription;
	private autoRetrySubscription?: Subscription;
	private countdownSubscription?: Subscription;

	constructor(private http: HttpClient, private router: Router, private configService: ConfigService) {}

	ngOnInit() {
		this.startAutoRetry();
	}

	ngOnDestroy() {
		this.clearSubscriptions();
	}

	retryConnection() {
		if (this.isRetrying) return;

		this.isRetrying = true;
		this.lastChecked = new Date();

		const url = this.configService.serviceUrl_Login + 'translate/' + this.configService.language;
		console.log(url);
		this.http
			.get(url, {
				observe: 'response',
				responseType: 'text',
			})
			.subscribe({
				next: (response: any) => {
					if (response.status === 200) {
						window.location.href = '/';
					}
					this.isRetrying = false;
				},
				error: (error: any) => {
					this.isRetrying = false;
					console.log('Server still down:', error);
				},
			});

		// Start countdown for next retry
		this.nextRetryIn = 10;
		this.countdownSubscription = interval(1000).subscribe(() => {
			this.nextRetryIn--;
			if (this.nextRetryIn <= 0) {
				this.countdownSubscription?.unsubscribe();
			}
		});
	}

	refreshPage() {
		window.location.reload();
	}

	startAutoRetry() {
		if (!this.autoRetryEnabled) return;

		this.autoRetryCountdown = 30;

		const countdown = interval(1000).subscribe(() => {
			this.autoRetryCountdown--;
			if (this.autoRetryCountdown <= 0) {
				countdown.unsubscribe();
				this.retryConnection();
				// Restart auto-retry cycle
				setTimeout(() => this.startAutoRetry(), 15000);
			}
		});

		this.autoRetrySubscription = countdown;
	}

	disableAutoRetry() {
		this.autoRetryEnabled = false;
		this.autoRetrySubscription?.unsubscribe();
	}

	private clearSubscriptions() {
		this.retrySubscription?.unsubscribe();
		this.autoRetrySubscription?.unsubscribe();
		this.countdownSubscription?.unsubscribe();
	}
}
